import { useEffect, useLayoutEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { ReactComponent as Apply } from '../../../../assets/icons/checkmark.svg'
import { ReactComponent as Close } from '../../../../assets/icons/close.svg'
import { ReactComponent as Delete } from '../../../../assets/icons/trash.svg'
import useIsFloaterInWrapperView from '../../../../hooks/useIsFloaterInWrapperView'
import useOnClickOutside from '../../../../hooks/useOnClickOutside'
import { IEntity, INLUDataExample } from '../../../../stores/nluDataStore'

import { joinClassNames } from '../../../../utils/styles'
import styles from './entities.module.scss'

interface IEntitiesProps {
    example: INLUDataExample
    editingEntities: IEntity[]
    handleStopEditingEntities: () => void
    setEditingEntities: React.Dispatch<React.SetStateAction<IEntity[]>>
    examplesRef: React.RefObject<HTMLDivElement>
    messageRef: React.RefObject<HTMLDivElement>
    allEntities: string[]
    setEntities: (entities: IEntity[]) => void
}

function Entities(props: IEntitiesProps) {
    const {
        example,
        handleStopEditingEntities,
        editingEntities,
        setEditingEntities,
        examplesRef,
        messageRef,
        allEntities,
        setEntities,
    } = props

    const { t } = useTranslation()
    const entitiesRef = useRef<HTMLDivElement>(null)

    useOnClickOutside(examplesRef, handleStopEditingEntities)

    const [{ isOverflowing }, { checkIfOverflows }] = useIsFloaterInWrapperView(
        examplesRef,
        entitiesRef,
        messageRef,
        10
    )

    const handleDeleteEntity = (entity: IEntity) => {
        setEditingEntities((prev) => {
            const copy = structuredClone(prev)

            return copy?.filter(
                (editingEntity) => editingEntity.start !== entity.start
            )
        })
    }

    useLayoutEffect(() => {
        checkIfOverflows()
    }, [editingEntities, checkIfOverflows])

    useEffect(() => {
        if (!editingEntities.length) {
            handleApplyEntities()
        }
    }, [editingEntities])

    const handleEditEntity = (
        e: React.ChangeEvent<HTMLInputElement>,
        entity: IEntity
    ) => {
        setEditingEntities((prev) => {
            const copy = structuredClone(prev)
            const entityToEdit = copy.find(
                (oldEntity) => oldEntity.value === entity.value
            )

            if (entityToEdit) {
                entityToEdit.entity = e.target.value
            }

            return copy
        })
    }

    const handleApplyEntities = () => {
        setEntities(editingEntities)
        handleStopEditingEntities()
    }

    return (
        <div
            className={joinClassNames(
                styles.entities,
                isOverflowing && styles.entitiesBottom
            )}
            ref={entitiesRef}
        >
            <div className={styles.entities__header}>
                <div className={styles.entities__headerText}>{t('text')}</div>
                <div className={styles.entities__headerText}>{t('entity')}</div>
                <div className={styles.entities__headerText}>{t('delete')}</div>
            </div>
            {editingEntities.map((editingEntity, index) => (
                <div key={index} className={styles.entities__entity}>
                    <div className={styles.entities__text}>
                        {example.text.slice(
                            editingEntity.start,
                            editingEntity.end
                        )}
                    </div>
                    <input
                        className={styles.entities__input}
                        list="intent"
                        name="intent"
                        value={editingEntity.entity}
                        onChange={(e) => handleEditEntity(e, editingEntity)}
                    />
                    <datalist id="intent">
                        {allEntities?.map((entity) => (
                            <option key={entity} value={entity}>
                                {entity}
                            </option>
                        ))}
                    </datalist>
                    <button
                        className={styles.entities__deleteBtn}
                        onClick={() => handleDeleteEntity(editingEntity)}
                    >
                        <Delete
                            className={styles.entities__deleteIcon}
                            alt={t('delete')}
                        />
                    </button>
                </div>
            ))}
            <div className={styles.entities__buttons}>
                <button
                    className={styles.entities__btn}
                    onClick={handleApplyEntities}
                >
                    <Apply className={styles.entities__icon} alt={t('apply')} />
                </button>
                <button
                    className={styles.entities__btn}
                    onClick={handleStopEditingEntities}
                >
                    <Close className={styles.entities__icon} alt={t('close')} />
                </button>
            </div>
        </div>
    )
}

export default Entities
